import { store } from "@/Store";
import { TestCommand } from "./TestInterfaces";
/**
 * Adds the specified commands to the list of commands to run.
 *
 * @param  {any[]} cmds  The commands to add.
 */
export function addTestsToCmdList(cmds) {
    const testsStr = store.state.test.cmds;
    const tests = testsStr === "" ? [] : JSON.parse(testsStr);
    tests.push(...cmds);
    store.commit("setVar", {
        name: "cmds",
        val: JSON.stringify(tests),
        module: "test",
    });
}
/**
 * A class to generate the command to type text into a selector.  Should only be
 * called from class TestCmdList. The parent that all test commands should
 * inherit from.
 */
export class TestCmdParent {
    /**
     * On rare occasions, you might want to add a test directly to the list,
     * outside of the TestCmdList system. Not recommended, but you can use this
     * if needed.
     */
    addToCmdList() {
        addTestsToCmdList([this.cmd]);
    }
}
/**
 * A class to generate the command to click a selector. Should only be called
 * from class TestCmdList.
 */
export class TestClick extends TestCmdParent {
    selector;
    shiftPressed;
    tourMessage;
    /**
     * Creates an instance of TestClick.
     *
     * @param {string} selector         The selector to click.
     * @param {boolean} [shiftPressed]  If true, the shift key will be pressed
     *                                  while clicking. Defaults to false.
     * @param {string} [tourMessage]    Optional custom message for tour steps,
     *                                  overriding the default "select it"
     *                                  phrasing.
     */
    constructor(selector, shiftPressed = false, tourMessage) {
        super();
        this.selector = selector;
        this.shiftPressed = shiftPressed;
        this.tourMessage = tourMessage;
    }
    /**
     * Generates the command to click the selector.
     *
     * @returns {ITestCommand} The command to click the selector.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.Click,
            data: this.shiftPressed,
            tourMessage: this.tourMessage,
        };
    }
}
/**
 * A class to generate the command to wait for a specified duration.  Should
 * only be called from class TestCmdList.
 */
export class TestWait extends TestCmdParent {
    duration;
    /**
     * Creates an instance of TestWait.
     *
     * @param  {number} [durationInSecs]  The duration to wait, in seconds.
     *                                    Defaults to 1.
     */
    constructor(durationInSecs = 1) {
        super();
        this.duration = durationInSecs;
    }
    /**
     * Generates the command to wait for the specified duration.
     *
     * @returns {ITestCommand}  The command to wait for the specified duration.
     */
    get cmd() {
        return {
            cmd: TestCommand.Wait,
            data: this.duration,
        };
    }
}
/**
 * A class to generate the command to type text into a selector.  Should only be
 * called from class TestCmdList.
 */
export class TestText extends TestCmdParent {
    selector;
    text;
    /**
     * Creates an instance of TestText.
     *
     * @param  {string} selector  The selector to type into.
     * @param  {string} text   The text to type.
     */
    constructor(selector, text) {
        super();
        this.selector = selector;
        this.text = text;
    }
    /**
     * Generates the command to type the specified text into the specified
     * selector.
     *
     * @returns {ITestCommand}  The command to type the specified text into the
     *  specified selector.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.Text,
            data: this.text,
        };
    }
}
/**
 * A class to generate the command to wait until the specified regex is found in
 * the specified selector. Should only be called from class TestCmdList.
 */
export class TestWaitUntilRegex extends TestCmdParent {
    selector;
    regex;
    timeout;
    /**
     * @param {string} selector  The element to watch.
     * @param {string} regex  The pattern that must appear in the element.
     * @param {number} [timeout]  Optional per-step wait override in seconds.
     *     Left undefined for the default; supplied for steps that can take
     *     longer than the standard wait (e.g. many structure downloads).
     */
    constructor(selector, regex, timeout) {
        super();
        this.selector = selector;
        this.regex = regex;
        this.timeout = timeout;
    }
    /**
     * Generates the command to wait until the specified regex is found in the
     * specified selector.
     *
     * @returns {ITestCommand}  The command to wait until the specified regex is
     * found in the specified selector.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.WaitUntilRegex,
            data: this.regex,
            timeout: this.timeout,
        };
    }
}
/**
 * A class to generate the command to wait until the specified regex is not
 * found in the specified selector. Should only be called from class
 * TestCmdList.
 */
export class TestWaitUntilNotRegex extends TestCmdParent {
    selector;
    regex;
    /**
     * Creates an instance of TestWaitUntilRegex.
     *
     * @param  {string} selector  The selector to monitor.
     * @param  {string} regex  The regex to wait for.
     */
    constructor(selector, regex) {
        super();
        this.selector = selector;
        this.regex = regex;
    }
    /**
     * Generates the command to wait until the specified regex is found in the
     * specified selector.
     *
     * @returns {ITestCommand}  The command to wait until the specified regex is
     * found in the specified selector.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.WaitUntilNotRegex,
            data: this.regex,
        };
    }
}
/**
 * A class to generate the command to upload a file. Should only be called from
 * class TestCmdList.
 */
export class TestUpload extends TestCmdParent {
    selector;
    filePath;
    /**
     * Creates an instance of TestUpload.
     *
     * @param  {string} selector  The selector to upload to.
     * @param  {string} filePath  The file path to upload.
     */
    constructor(selector, filePath) {
        super();
        this.selector = selector;
        if (filePath.startsWith("file://")) {
            filePath = filePath.substring(7);
        }
        this.filePath = filePath;
    }
    /**
     * Generates the command to upload the specified file to the specified
     * selector.
     *
     * @returns {ITestCommand}  The command to upload the specified file to the
     * specified selector.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.Upload,
            data: this.filePath,
        };
    }
}
/**
 * A class to generate the command for displaying a note during a tour. Should
 * only be called from class TestCmdList.
 */
export class TestTourNote extends TestCmdParent {
    selector;
    message;
    /**
     * Creates an instance of TestTourNote.
     *
     * @param {string} selector  The selector for the element to associate the
     *           note with.
     * @param {string} message   The message to display in the note.
     */
    constructor(selector, message) {
        super();
        this.selector = selector;
        this.message = message;
    }
    /**
     * Generates the command to display the tour note.
     *
     * @returns {ITestCommand}  The command.
     */
    get cmd() {
        return {
            selector: this.selector,
            cmd: TestCommand.TourNote,
            data: this.message,
        };
    }
}
